'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { TrackComponent } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import type { SegmentKey } from '@eliadmin/core/src/components/product-segments/product-segments';
import { ProductCarousel } from '@eliadmin/core/src/components/product/product-carousel';
import { Container } from '@eliadmin/core/src/components/ui/container';
import { Skeleton } from '@eliadmin/core/src/components/ui/skeleton';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import {
  buildProductsQuery,
  type BuildProductQueryOptions,
} from '@eliadmin/core/src/utils/product-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Suspense, useMemo, type ComponentPropsWithoutRef } from 'react';

export type ProductsContainerProps = {
  query?: BuildProductQueryOptions;
  title?: string;
  id?: string;
  className?: string;
  before?: React.ReactNode;
  after?: React.ReactNode;
  style?: React.CSSProperties;
  carouselProps?: Omit<ComponentPropsWithoutRef<typeof ProductCarousel>, 'products'>;
  children?: React.ReactNode;
  segmentKey?: SegmentKey;
};

function ProductsContainerInternal({ query: queryOpts, ...props }: ProductsContainerProps) {
  const locale = useLocaleStore(s => s.currentLocale);
  const { me } = useCustomerStore();
  const templateSettings = useGlobalStore(s => s.templateSettings);

  const query = useMemo(() => buildProductsQuery({ ...queryOpts }), [queryOpts]);
  const { data: products } = useSuspenseQuery({
    queryKey: [
      CacheKey.PRODUCTS,
      locale,
      props.segmentKey ?? null,
      query,
      me?.customer.id ?? null,
    ],
    queryFn: async () => {
      if (templateSettings?.homepage?.homepage_segments?.enabled && props.segmentKey) {
        const { fetchProductSegmentsProductsBySegmentType } = await import(
          '@eliadmin/core/src/api/product-segments/product-segments-api'
        );
        const segmentsResponse = await fetchProductSegmentsProductsBySegmentType({
          locale,
          segmentType: props.segmentKey,
          query,
          authToken: me?.authToken,
        });
        if (segmentsResponse.data.length > 0) {
          return segmentsResponse;
        } else {
          return fetchProducts({ query, locale, authToken: me?.authToken }).catch(() => {
            return { data: [] };
          });
        }
      } else {
        return fetchProducts({ query, locale, authToken: me?.authToken }).catch(() => {
          return { data: [] };
        });
      }
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  if (!products?.data?.length) return null;

  return (
    <section id={props.id} className={cn(props.className)} style={props.style}>
      <Container size="xl" className="flex flex-col space-y-6">
        <TrackComponent
          eventName={'view_item_list'}
          items={products.data.map(product => {
            const priceDisplay = getProductPriceDisplay(product);

            return {
              item_id: product.code,
              item_name: product.name,
              price: priceDisplay.priceOnRequest ? undefined : priceDisplay.currentWithVat,
              quantity: 1,
            };
          })}
        />
        <h2 className="w-full text-left text-2xl font-bold">{props.title}</h2>
        <div className="flex flex-row items-stretch gap-4">
          {props.before}
          <ProductCarousel {...props.carouselProps} products={products.data} />
          {props.after}
        </div>
        {props.children}
      </Container>
    </section>
  );
}

export function ProductsContainer(props: ProductsContainerProps) {
  const loader = (
    <section id={props.id} className={cn(props.className)} style={props.style}>
      <Container size="xl" className="flex flex-col space-y-6">
        <h2 className="w-full px-2 text-left text-2xl font-bold sm:px-4">{props.title}</h2>
        <div className="flex flex-row items-stretch gap-4">
          {props.before}
          <div className={cn('flex w-full flex-row items-stretch gap-4 overflow-x-hidden')}>
            <Skeleton className="h-92 w-64 min-w-[16rem] shadow-lg" />
          </div>
          {props.after}
        </div>
      </Container>
    </section>
  );

  return (
    <Suspense fallback={loader}>
      <ProductsContainerInternal {...props} />
    </Suspense>
  );
}
